import React, { useEffect } from "react";
import { Tooltip } from "@/components/common/Tooltip";

import { SidebarDropdown } from "./SidebarDropdown";
import { useSidebarFlyout } from "./useSidebarFlyout";
import type { DropdownItem } from "./SidebarDropdown";
import { useSidebarOverlayStore } from "stores/useSidebarOverlayStore";

export interface SidebarItem {
  id?: string;
  icon: React.ReactNode;
  text: string;
  secondaryText?: string;
  page?: string;
  hasDropdown?: boolean;
  onHover?: () => void;
  onLeave?: () => void;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
  isActive?: boolean;
  tooltip?: string;
  rightAction?: React.ReactNode;
  /// Controls of their own, so they sit beside the row's button, not in it.
  /// Reveal them with `group-hover/rail-item`.
  trailing?: React.ReactNode;
  /// A control laid over the row's icon, which it draws itself.
  leading?: React.ReactNode;
  children?: SidebarItem[];
  /// A child's depth in a tree its parent's flyout lists.
  depth?: number;
  customContent?: React.ReactNode;
}

interface SidebarItemsProps {
  items: SidebarItem[];
  isOpen: boolean;
  activeItem?: string;
  onItemClick?: (page: string) => void;
  variant?: "default" | "profile";
  sidebarWidth?: string;
}

export const SidebarItems: React.FC<SidebarItemsProps> = ({
  items,
  isOpen,
  activeItem,
  onItemClick,
  variant = "default",
  sidebarWidth,
}) => {
  const isProfile = variant === "profile";
  const overlayOpen = useSidebarOverlayStore((s) => s.count > 0);

  const {
    flyout: childrenDropdown,
    show: showChildrenDropdown,
    hideSoon,
    hide,
    onHoverChange,
  } = useSidebarFlyout<number>();
  useEffect(() => hide(), [isOpen, hide]);

  const activeDropdownParent = childrenDropdown
    ? items[childrenDropdown.key]
    : null;
  const dropdownItems: DropdownItem[] =
    activeDropdownParent?.children?.map((child) => ({
      id: child.id || child.page || child.text,
      label: child.text,
      icon: child.icon,
      rightIcon: child.rightAction,
      depth: child.depth,
      onClick: () => {
        if (child.onClick) child.onClick();
        else if (child.page && onItemClick) onItemClick(child.page);
        hide();
      },
      selected:
        child.isActive ??
        (activeItem
          ? activeItem === child.text || activeItem === child.page
          : false),
    })) || [];

  return (
    <>
      <ul className={`flex flex-col`}>
        {items.map((item, index) => {
          const itemActive =
            item.isActive ??
            (activeItem
              ? activeItem === item.text || activeItem === item.page
              : false);
          const hasSecondaryText = !!item.secondaryText;
          // A parent's children are ALWAYS a side flyout, open or collapsed —
          // expanding them inline pushes the pinned bottom stack off-screen.
          const hasChildrenDropdown = !!item.children?.length;

          const buttonContent = (
            <button
              id={item.id}
              ref={item.buttonRef}
              aria-current={itemActive && item.page ? "page" : undefined}
              onClick={(e) => {
                // Opens the flyout for keyboard and touch, which get no
                // hover. Deliberately not a toggle: `mouseenter` fires first
                // on a mouse, so toggling would close what the hover just
                // opened. An item with a page of its own goes there instead.
                const ownsDestination = !!item.onClick || !!item.page;
                if (hasChildrenDropdown && !ownsDestination) {
                  showChildrenDropdown(index, e.currentTarget);
                  return;
                }
                if (hasChildrenDropdown) hide();
                if (item.onClick) {
                  item.onClick(e);
                } else if (item.page && onItemClick) {
                  onItemClick(item.page);
                }
              }}
              onMouseEnter={(e) => {
                item.onHover?.();
                if (hasChildrenDropdown) {
                  showChildrenDropdown(index, e.currentTarget);
                }
              }}
              onMouseLeave={() => {
                item.onLeave?.();
                if (hasChildrenDropdown) {
                  hideSoon();
                }
              }}
              className={`
                group w-full flex items-center rounded-app relative focus:outline-none transition-colors
                ${isProfile ? "py-1 cursor-pointer" : "item-height"}
                ${itemActive ? "bg-list-selected-light" : "group-hover/rail-item:bg-list-hover"}
              `}
              style={{ transitionDuration: "var(--duration-micro)" }}
            >
              <div className={`flex items-center min-w-0`}>
                <span
                  className={`shrink-0 flex items-center justify-center w-8 h-8 ${
                    item.leading && isOpen ? "invisible" : ""
                  }`}
                >
                  {item.icon}
                </span>

                <div
                  className={`
                    whitespace-nowrap pr-2
                    ${hasSecondaryText || item.customContent ? "flex flex-col items-start justify-center" : "flex items-center"}
                  `}
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <span
                    className={`truncate ${hasSecondaryText ? "w-full" : ""} text-fg-regular text-left ${isProfile ? "cursor-pointer" : "cursor-default"}`}
                  >
                    {item.text}
                  </span>
                  {hasSecondaryText && (
                    <span className="text-fg-muted-regular truncate w-full text-left cursor-default">
                      {item.secondaryText}
                    </span>
                  )}
                  {item.customContent && (
                    <div className="w-full">{item.customContent}</div>
                  )}
                </div>
              </div>

              {item.rightAction && isOpen && (
                <div
                  className={`shrink-0 ml-auto pr-1 ${
                    item.children && !isProfile
                      ? ""
                      : "transition-opacity invisible opacity-0 group-hover:visible group-hover:opacity-100"
                  }`}
                >
                  {item.rightAction}
                </div>
              )}
            </button>
          );

          // Unmount the tooltip entirely while an overlay is open — merely
          // disabling the hover listener leaves an already-open tooltip
          // hanging on top of the overlay.
          const tooltipSuppressed =
            isOpen || overlayOpen || !item.tooltip || !!item.children;

          return (
            <li key={index} className="group/rail-item relative w-full">
              {tooltipSuppressed ? (
                buttonContent
              ) : (
                <Tooltip title={item.tooltip ?? ""} placement="right">
                  {buttonContent}
                </Tooltip>
              )}
              {item.leading && isOpen && (
                <div className="absolute inset-y-0 left-0 flex items-center">
                  {item.leading}
                </div>
              )}
              {item.trailing && isOpen && (
                <div className="absolute inset-y-0 right-1 flex items-center">
                  {item.trailing}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {childrenDropdown && sidebarWidth && (
        <SidebarDropdown
          items={dropdownItems}
          isVisible={true}
          topPosition={childrenDropdown.top}
          sidebarWidth={sidebarWidth}
          maxWidth="var(--sidebar-width-open)"
          onHoverChange={onHoverChange}
        />
      )}
    </>
  );
};
